import { useIsMutating } from '@tanstack/react-query';
import type { ListAuditQuestionResponse } from 'src/types/helios/QuestionResponse';
import { useAuditQuestionResponsesQuery } from './useAuditQuestionResponsesQuery';

export interface UseFreshAuditQuestionResponsesOptions {
  readonly businessUnitId: string | undefined;
  readonly auditId: string | undefined;
}

export interface FreshAuditQuestionResponsesResult {
  /** Primary-table response list, or undefined until the read lands. */
  readonly data: ListAuditQuestionResponse | undefined;
}

/**
 * Primary-table (DynamoDB) response list for one audit, paused while
 * any save is in flight.
 *
 * Composes {@link useAuditQuestionResponsesQuery} with the same
 * in-flight-mutation gate {@link useAuditQuestionResponsePolling}
 * applies, so both primary-table reads follow one rule instead of two
 * copies drifting apart. Without the gate, a read that resolves
 * mid-save hands the draft hooks a server value that predates the
 * in-flight write, briefly reverting what the auditor just entered.
 *
 * Lives in the data layer rather than the page so the page doesn't
 * reach for React Query internals, and so page tests that mock this
 * barrel don't need a QueryClientProvider.
 */
export function useFreshAuditQuestionResponses(
  options: UseFreshAuditQuestionResponsesOptions,
): FreshAuditQuestionResponsesResult {
  const { businessUnitId, auditId } = options;
  const isMutating = useIsMutating() > 0;
  const { data } = useAuditQuestionResponsesQuery(
    { businessUnitId: businessUnitId ?? '', auditId: auditId ?? '' },
    {
      enabled: !!businessUnitId && !!auditId && !isMutating,
      // Always go to the wire, same as the poller's `fetchQuery`.
      //
      // The app default is a 30s `staleTime` (see MobileShell), and
      // re-enabling a query does NOT refetch while its cached data is
      // still within that window — React Query treats the cache as
      // fresh. So without this, the mutation gate above would reopen
      // after a save and immediately serve the PRE-WRITE snapshot,
      // and nothing invalidates this key on save (the mutation
      // deliberately invalidates on error only). The point of reading
      // the primary table is that it reflects a write immediately;
      // serving a 30s-old list would forfeit exactly that.
      staleTime: 0,
    },
  );
  return { data };
}
